import {effect, inject, Injectable, signal} from '@angular/core';
import {OAuthEvent, OAuthService} from "angular-oauth2-oidc";
import {oidcConfig} from "./oidc.config";
import {JwksValidationHandler} from "angular-oauth2-oidc-jwks";
import {jwtDecode} from "jwt-decode";
import {AlertService} from "../alert/alert.service";
import {filter, tap} from "rxjs";

const ROLE_ADMIN = 'green-x-energy-hub-syneco-admin';
const ROLE_AGGREGATOR = 'green-x-energy-hub-aggregator';
const ROLE_OPERATOR = 'green-x-energy-hub-plant-operator';

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  private oauthService = inject(OAuthService);
  private alertService = inject(AlertService);

  private _loggedIn = signal<boolean>(false);
  public readonly isLoggedIn = this._loggedIn.asReadonly();

  private isInitialized = false;

  constructor() {
    this.configure();

    this._loggedIn.set(this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken());

    this.oauthService.events
      .pipe(
        tap(() => {
          this._loggedIn.set(this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken());
        }),
        filter((e: OAuthEvent) => {
          console.log('oauth event', e);
          return [
            'token_error',
            'token_refresh_error',
            'silent_refresh_error',
            'session_error',
            'session_terminated',
          ].includes(e.type);
        })
      )
      .subscribe(() => {
        this.alertService.showAlert(
          'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.'
        );
        this.logOut();
      });

    effect(() => {
      if (this._loggedIn()) {
        if (!this.hasAnyGreenXRole()) {
          console.log('alerting!!!!');
          this.alertService.showAlert('Bitte kontaktieren Sie den Administrator, um die erforderlichen Rollen zu erhalten.');
        }
      }
    });

  }

  public isAdmin(): boolean {
    return this.hasRole(ROLE_ADMIN);
  }

  public isAggregator(): boolean {
    return this.hasRole(ROLE_AGGREGATOR);
  }

  public isAdminOrAggregator(): boolean {
    return this.isAdmin() || this.isAggregator();
  }

  async initAuth(): Promise<void> {
    if (this.isInitialized) return;

    try {
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();
      this.isInitialized = true;

      // If we have tokens after login attempt, user is authenticated
      if (this.oauthService.hasValidAccessToken()) {
        console.log('User authenticated');
      }
    } catch (error) {
      console.error('Auth initialization failed:', error);
    }
  }

  public configure() {
    this.oauthService.configure(oidcConfig);
    this.oauthService.setStorage(localStorage);
    this.oauthService.tokenValidationHandler = new JwksValidationHandler();
    this.oauthService.setupAutomaticSilentRefresh();
  }

  get username(): string {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) return '';
    return claims['given_name'];
  }

  get fullName(): string {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) return '';
    return claims['name'];
  }

  get familyName(): string {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) return '';
    return claims['family_name'];
  }

  get email(): string {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) return '';
    return claims['email'];
  }

  get companyId() {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) return '';
    return claims['company-id'];
  }

  logIn(): void {
    this.oauthService.initCodeFlow();
  }

  logOut(): void {
    this.oauthService.state = '';
    localStorage.removeItem('nonce');
    this.oauthService.logOut();
    this._loggedIn.set(false);
  }

  private hasAnyGreenXRole(): boolean {
    return this.hasRole(ROLE_ADMIN) ||
      this.hasRole(ROLE_AGGREGATOR) ||
      this.hasRole(ROLE_OPERATOR);
  }

  private hasRole(role: string): boolean {
    const realmAccess = this.getRealmAccess();
    if (realmAccess != null && realmAccess.roles != null) {
      return realmAccess.roles.some((x: string) => x === role);
    }
    return false;
  }

  private getRealmAccess(): any {
    if (!this.oauthService.hasValidAccessToken()) {
      return null;
    }

    try {
      const token = this.oauthService.getAccessToken();
      const decoded = jwtDecode<Token>(token) as unknown as Partial<Token> | null | undefined;
      const realmAccess = decoded && (decoded as any).realm_access;
      if (realmAccess && Array.isArray((realmAccess as any).roles)) {
        return realmAccess;
      }
      return null;
    } catch (e) {
      // Malformed/unknown token payloads must never crash the app/tests
      return null;
    }
  }
}

type Token = {
  realm_access?: {
    roles?: string[];
  };
}
